import { useGetList } from "ra-core";
import { useSearchParams } from "react-router";
import type { ReactNode } from "react";
import { BriefcaseBusiness, House, Landmark, UserPlus } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import type { Contact, ContactNote } from "../types";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardStepper } from "./DashboardStepper";
import { LoadsChart } from "./LoadsChart";
import {
  AccountManagerDashboard,
  AccountingDashboard,
  LeadGenDashboard,
} from "./RoleDashboards";
import { TasksList } from "./TasksList";
import { Welcome } from "./Welcome";

const HomeDashboard = () => {
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });

  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });

  const { total: totalLoad, isPending: isPendingLoad } = useGetList<Contact>(
    "loads",
    {
      pagination: { page: 1, perPage: 1 },
    },
  );

  const isPending = isPendingContact || isPendingContactNotes || isPendingLoad;

  if (isPending) {
    return null;
  }

  if (!totalContact) {
    return <DashboardStepper step={1} />;
  }

  if (!totalContactNotes) {
    return <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-1">
      <div className="md:col-span-9">
        <div className="flex flex-col gap-6">
          {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}
          {totalLoad ? <LoadsChart /> : null}
          <DashboardActivityLog />
        </div>
      </div>

      <div className="md:col-span-3">
        <TasksList />
      </div>
    </div>
  );
};

export const Dashboard = () => <DashboardTabs />;

export const DashboardTabs = ({ home }: { home?: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDashboard = searchParams.get("dashboard");
  const dashboard =
    requestedDashboard &&
    ["home", "lead-gen", "account-manager", "accounting"].includes(
      requestedDashboard,
    )
      ? requestedDashboard
      : "home";

  return (
    <Tabs
      value={dashboard}
      onValueChange={(value) =>
        setSearchParams((previous) => {
          const next = new URLSearchParams(previous);
          next.set("dashboard", value);
          return next;
        })
      }
      className="mt-1 gap-5"
    >
      <TabsList className="grid h-11 w-full grid-cols-4 sm:w-fit sm:min-w-[600px]">
        <TabsTrigger value="home">
          <House /> Home
        </TabsTrigger>
        <TabsTrigger value="lead-gen" aria-label="Lead Generation">
          <UserPlus />
          <span className="hidden sm:inline">Lead Gen</span>
          <span className="sm:hidden">Leads</span>
        </TabsTrigger>
        <TabsTrigger value="account-manager" aria-label="Account Manager">
          <BriefcaseBusiness />
          <span className="hidden sm:inline">Account Manager</span>
          <span className="sm:hidden">Manager</span>
        </TabsTrigger>
        <TabsTrigger value="accounting">
          <Landmark /> Accounting
        </TabsTrigger>
      </TabsList>
      <TabsContent value="home">{home ?? <HomeDashboard />}</TabsContent>
      <TabsContent value="lead-gen">
        <LeadGenDashboard />
      </TabsContent>
      <TabsContent value="account-manager">
        <AccountManagerDashboard />
      </TabsContent>
      <TabsContent value="accounting">
        <AccountingDashboard />
      </TabsContent>
    </Tabs>
  );
};
